<script setup lang="ts">
import type { MobileMenuProps } from "./MobileMenuProps";
import EditableElement from "@/views/page/EditableElement.vue";
import LogoComponent from "@/views/blocks/navigation/shared/logo/LogoComponent.vue";
import PrimaryButton from "@/views/blocks/shared/section/buttons/primary-button/PrimaryButton.vue";
import SecondaryButton from "@/views/blocks/shared/section/buttons/secondary-button/SecondaryButton.vue";
import DialogMenu from "@/views/blocks/common/DialogMenu.vue";
import OpenCloseIcon from "@/views/blocks/common/OpenCloseIcon.vue";
import { handleHashNavigation } from "@/shared/utils/router/hashNavigation";

const { button, callToActionButton, isOpen, items, logo, styleValues } =
    defineProps<MobileMenuProps>();

const emit = defineEmits<{
    close: [];
}>();

const handleClose = () => {
    emit("close");
};
</script>

<template>
    <DialogMenu :is-open="isOpen" :on-close="handleClose" position="right" max-width="20rem">
        <div
            class="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto p-4 lg:px-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10"
            :style="{
                backgroundColor: styleValues.background,
                '--text-color': styleValues.text,
            }"
        >
            <div class="flex items-center justify-between">
                <LogoComponent v-bind="logo.data" />
                <div class="flex items-center gap-6">
                    <EditableElement v-if="!callToActionButton.isHidden">
                        <PrimaryButton
                            :label="callToActionButton.data.label"
                            :href="callToActionButton.data.href"
                            :background-color="styleValues.brand"
                            :text-color="styleValues.background"
                            class="lg:block px-4 py-2.5 rounded-lg text-sm shadow-xs"
                        />
                    </EditableElement>
                    <button type="button" @click="handleClose">
                        <OpenCloseIcon :is-open="true" />
                    </button>
                </div>
            </div>
            <div class="divide-y divide-gray-500/10">
                <div class="space-y-2 py-6">
                    <a
                        v-for="item in items.data.items"
                        :key="item.id"
                        :href="item.href"
                        class="block link-item py-2 text-base"
                        @click="handleHashNavigation"
                    >
                        {{ item.label }}
                    </a>
                </div>
                <div class="py-6">
                    <EditableElement v-if="!button.isHidden">
                        <SecondaryButton
                            :label="button.data.label"
                            :href="button.data.href"
                            :text-color="styleValues.text"
                            :show-arrow="false"
                            class="text-sm"
                        />
                    </EditableElement>
                </div>
            </div>
        </div>
    </DialogMenu>
</template>

<style scoped>
a.link-item {
    color: var(--text-color);
    border-bottom: 1px solid transparent;
}

a.link-item:hover {
    border-bottom: 1px solid var(--text-color);
}

a.link-item:active {
    border-bottom: 1px solid color-mix(in srgb, var(--text-color), transparent);
}

a.link-item:focus-visible,
.close-button:focus-visible {
    outline: 1px dotted var(--text-color);
    outline-offset: 2px;
}

.close-button {
    color: var(--text-color);
}

.close-button:hover {
    color: color-mix(in srgb, var(--text-color) 90%, transparent);
}
</style>
